import { fmt, fmtNum, fmtDate } from '../../utils';

/** Counts are whole things (lines, vendors, vouchers) — the shared fmtNum
 *  forces 2 decimals, which reads wrong for them ("645.00 Lines"). */
export const fmtCount = (n) => (Number(n) || 0).toLocaleString('en-NG');

// ── Group modes ────────────────────────────────────────────────
// The four primary pills, then the extras behind "More Groups".
export const GROUP_MODES = [
  { key: 'department_name', label: 'Department' },
  { key: 'project_name', label: 'Project' },
  { key: 'vendor_name', label: 'Vendor' },
  { key: 'gl_code', label: 'GL Code' },
];

export const MORE_GROUP_MODES = [
  { key: 'voucher_no', label: 'Voucher Ref' },
  { key: 'manual_code', label: 'Manual Code' },
  { key: 'req_ref', label: 'Req Ref' },
  { key: 'invoice_no', label: 'Invoice No.' },
  { key: 'waybill_no', label: 'Waybill No.' },
  { key: 'payment_method', label: 'Mode of Payment' },
];

export const ALL_GROUP_MODES = [...GROUP_MODES, ...MORE_GROUP_MODES];

export const groupModeLabel = (key) =>
  (ALL_GROUP_MODES.find(m => m.key === key) || {}).label || key;

const UNASSIGNED = 'Unassigned';

const distinct = (rows, key) =>
  [...new Set(rows.map(r => r[key]).filter(Boolean))];

/** One value → show it; many → "N Vendors". Mirrors the reference summary rows. */
export const distinctLabel = (values, singular, plural) => {
  if (values.length === 0) return '—';
  if (values.length === 1) return values[0];
  return `${values.length} ${plural || `${singular}s`}`;
};

const sum = (rows, key) => rows.reduce((t, r) => t + (Number(r[key]) || 0), 0);

/** Collapse lines into group buckets, richest first. */
export const groupLines = (lines, groupKey) => {
  const buckets = new Map();
  lines.forEach(line => {
    const key = line[groupKey] || UNASSIGNED;
    if (!buckets.has(key)) buckets.set(key, []);
    buckets.get(key).push(line);
  });

  return [...buckets.entries()]
    .map(([key, rows]) => ({
      key,
      rows,
      lineCount: rows.length,
      vouchers: distinct(rows, 'voucher_no'),
      requisitions: distinct(rows, 'req_ref'),
      projects: distinct(rows, 'project_name'),
      vendors: distinct(rows, 'vendor_name'),
      departments: distinct(rows, 'department_name'),
      sampleDescription: rows[0]?.expense_name || rows[0]?.description || '—',
      totalAmount: sum(rows, 'amount'),
      totalPayable: sum(rows, 'amount_payable'),
      totalPaid: sum(rows, 'amount_paid'),
    }))
    .sort((a, b) => b.totalAmount - a.totalAmount);
};

// ── Filtering ──────────────────────────────────────────────────
const SEARCH_FIELDS = [
  'voucher_no', 'manual_code', 'req_ref', 'material_code', 'vendor_name',
  'waybill_no', 'boq_code', 'sub_boq_code', 'description', 'expense_name',
  'invoice_no', 'ref_no', 'boq_description',
];

export const applyFilters = (lines, f) => lines.filter(l => {
  if (f.project && l.project_name !== f.project) return false;
  if (f.vendor && l.vendor_name !== f.vendor) return false;
  if (f.department && l.department_name !== f.department) return false;
  if (f.paymentMethod && l.payment_method !== f.paymentMethod) return false;
  if (f.accountLedger && l.account_ledger_name !== f.accountLedger) return false;
  if (f.purchaseType && l.purchase_type !== f.purchaseType) return false;
  if (f.status && l.status !== f.status) return false;
  if (f.dateFrom && (!l.voucher_date || l.voucher_date < f.dateFrom)) return false;
  if (f.dateTo && (!l.voucher_date || l.voucher_date > f.dateTo)) return false;
  if (f.search) {
    const q = f.search.toLowerCase();
    const hit = SEARCH_FIELDS.some(k => String(l[k] || '').toLowerCase().includes(q));
    if (!hit) return false;
  }
  return true;
});

/** Distinct, sorted values for a filter dropdown. */
export const filterOptions = (lines, key) =>
  [...new Set(lines.map(l => l[key]).filter(Boolean))].sort();

// ── Detail columns ─────────────────────────────────────────────
// The reference lists BOQ twice ("BOQ" and "BOQ Codes"); collapsed to one
// here since both render the same value.
export const DETAIL_COLUMNS = [
  { key: 'voucher_date', label: 'Date', render: v => fmtDate(v) },
  { key: 'voucher_no', label: 'Voucher Ref', mono: true },
  { key: 'manual_code', label: 'Manual Code' },
  { key: 'req_ref', label: 'Req Ref', mono: true },
  { key: 'status', label: 'Status', isStatus: true },
  { key: 'vendor_name', label: 'Vendor' },
  { key: 'expense_name', label: 'Expense Name' },
  { key: 'material_code', label: 'Material Code', mono: true },
  { key: 'description', label: 'Description', wide: true },
  { key: 'brand', label: 'Brand' },
  { key: 'unit', label: 'Unit' },
  { key: 'qty', label: 'Qty', align: 'right', render: v => fmtNum(v) },
  { key: 'rate', label: 'Rate', align: 'right', render: v => fmt(v) },
  { key: 'amount', label: 'Amount', align: 'right', render: v => fmt(v), bold: true },
  { key: 'vat', label: 'VAT', align: 'right', render: v => fmt(v) },
  { key: 'wht', label: 'WHT', align: 'right', render: v => fmt(v) },
  { key: 'discount', label: 'Discount', align: 'right', render: v => fmt(v) },
  { key: 'amount_payable', label: 'Amt Payable', align: 'right', render: v => fmt(v), bold: true },
  // Amt Paid — hidden on request. This entry also feeds buildCsvRows, so
  // uncommenting restores the column in the expanded rows AND in the CSV
  // export together.
  // { key: 'amount_paid', label: 'Amt Paid', align: 'right', render: v => fmt(v), bold: true },
  { key: 'gl_code', label: 'GL', mono: true },
  { key: 'account_ledger_name', label: 'Account Ledger' },
  { key: 'boq_code', label: 'BOQ Code', mono: true },
  { key: 'sub_boq_code', label: 'Sub BOQ', mono: true },
  { key: 'boq_description', label: 'BOQ Description', wide: true },
  { key: 'project_name', label: 'Project' },
  { key: 'department_name', label: 'Department' },
  { key: 'payment_method', label: 'Payment Method' },
  { key: 'purchase_type', label: 'Material / Purchase Type' },
  { key: 'invoice_no', label: 'Invoice No' },
  { key: 'waybill_no', label: 'Waybill No.' },
  { key: 'ref_no', label: 'Ref No.', mono: true },
];

/** Flat rows keyed by display header — exportCSV derives headers from keys. */
export const buildCsvRows = (lines) => lines.map(l => {
  const row = {};
  DETAIL_COLUMNS.forEach(c => {
    const raw = l[c.key];
    // Money/qty stay numeric so the CSV stays spreadsheet-friendly.
    row[c.label] = raw === null || raw === undefined ? '' : raw;
  });
  return row;
});

export const STATUS_STYLES = {
  approved: { bg: '#dbeafe', color: '#1d4ed8', label: 'Approved' },
  posted: { bg: '#dcfce7', color: '#15803d', label: 'Posted' },
};
